import { requiresExplicitAnalyticsDates } from '@/lib/analyticsDateRange';
import { jsonInit, nullableOptional, toSearchParams } from '@shigo-ui/api-client';
import { z } from 'zod';

import { createShigoApiClient, type ShigoApiConfig } from '../../../lib/shigo-api-client';
import { reportResponseSchema, type ReportRequest, type ReportFilterOption } from './model';
import { reportShareResponseSchema, type CreateReportSharePayload } from './reportShare';
import { demandPartnerBidderCodes } from '../../demand/partners/bidderCodes';
import { reportFilterDefinitions } from './reportFilters';
import {
  ReportExplorerScope,
  ReportFilterField,
  ReportFilterLookupMode,
  ReportFilterLookupStatus,
} from './reportEnums';

interface ReportExplorerClientOptions {
  config: ShigoApiConfig;
  accessToken: string;
  fetcher?: typeof fetch;
}

const lookupIdentifierSchema = z.union([z.string(), z.number()]).transform(String);

const lookupRecordSchema = z.looseObject({
  id: nullableOptional(lookupIdentifierSchema),
  numericId: nullableOptional(lookupIdentifierSchema),
  secondaryId: nullableOptional(lookupIdentifierSchema),
  name: nullableOptional(z.string()),
  displayName: nullableOptional(z.string()),
  url: nullableOptional(z.string()),
  bundleId: nullableOptional(z.string()),
  countryIsoCode: nullableOptional(z.string()),
  countryName: nullableOptional(z.string()),
});
const lookupPageSchema = z.looseObject({ content: z.array(lookupRecordSchema) });
const lookupListSchema = z.union([
  z.array(lookupRecordSchema),
  z.looseObject({ content: z.array(lookupRecordSchema) }).transform(({ content }) => content),
]);
const stringListSchema = z.array(z.string());

export function createReportExplorerClient({
  config,
  accessToken,
  fetcher,
}: ReportExplorerClientOptions) {
  const api = createShigoApiClient(config, accessToken, fetcher);

  return {
    createShare(payload: CreateReportSharePayload) {
      return api.request({
        path: '/api/report-url',
        schema: reportShareResponseSchema,
        init: jsonInit('POST', payload),
      });
    },
    getShare(urlHash: string) {
      return api.request({
        path: `/api/report-url/${encodeURIComponent(urlHash)}`,
        schema: reportShareResponseSchema,
      });
    },
    run(
      request: ReportRequest,
      scope = ReportExplorerScope.Standard,
      agencyId?: string,
      signal?: AbortSignal,
    ) {
      let path =
        scope === ReportExplorerScope.Publisher ? '/api/publishers/report' : '/api/report/ch';
      if (scope === ReportExplorerScope.Agency) {
        if (!agencyId) throw new Error('Agency ID is required.');
        path = `/api/agency/${encodeURIComponent(agencyId)}/report`;
      }
      return api.request({
        path,
        schema: reportResponseSchema,
        init: {
          ...jsonInit(
            'POST',
            requiresExplicitAnalyticsDates(request.filters.dateRange)
              ? { ...request, filters: { ...request.filters, dateRange: null } }
              : request,
          ),
          ...(signal ? { signal } : {}),
        },
      });
    },
    async searchFilterOptions(
      field: ReportFilterField,
      searchTerm: string,
      scope = ReportExplorerScope.Standard,
      publisherId?: string,
      demandIntegrationId?: string,
    ) {
      if (reportFilterDefinitions[field].lookupMode !== ReportFilterLookupMode.Server) {
        throw new Error(`Report filter ${field} does not use server search.`);
      }
      if (field === ReportFilterField.BundleId) {
        const values = await api.request({
          path: `/api/apps/bundles?${toSearchParams({
            page: 0,
            size: 50,
            bundleId: searchTerm,
            ...(scope === ReportExplorerScope.Publisher && publisherId
              ? { publisherIds: [publisherId] }
              : {}),
          })}`,
          schema: stringListSchema,
        });
        return values.map((value) => ({ id: value, label: value }));
      }
      if (field === ReportFilterField.Domain && scope === ReportExplorerScope.Publisher) {
        const response = await api.request({
          path: `/api/publishers/report/domains?${toSearchParams({
            page: 0,
            size: 50,
            url: searchTerm,
            ...(publisherId ? { publisherIds: [publisherId] } : {}),
          })}`,
          schema: lookupPageSchema,
        });
        return toLookupOptions(field, response.content);
      }
      if (field === ReportFilterField.Country) {
        const values = await api.request({
          path: `/api/targetings/geolocations/countries/search?${toSearchParams({ searchTerm })}`,
          schema: z.array(lookupRecordSchema),
        });
        return toLookupOptions(field, values);
      }
      const response = await api.request({
        path: reportFilterSearchPath(field, searchTerm, demandIntegrationId),
        schema: lookupPageSchema,
      });
      return toLookupOptions(field, response.content);
    },
    async listFilterOptions(field: ReportFilterField): Promise<ReportFilterOption[]> {
      if (reportFilterDefinitions[field].lookupMode !== ReportFilterLookupMode.Local) {
        throw new Error(`Report filter ${field} does not use a local option list.`);
      }
      if (field === ReportFilterField.BidderCode) {
        return demandPartnerBidderCodes.map((value) => ({ id: value, label: value }));
      }
      const values = await api.request({
        path: '/api/apps/platforms',
        schema: lookupListSchema,
      });
      return toLookupOptions(field, values);
    },
  };
}

function reportFilterSearchPath(
  field: ReportFilterField,
  searchTerm: string,
  demandIntegrationId?: string,
) {
  const common = { page: 0, size: 50 };
  switch (field) {
    case ReportFilterField.Agency:
      return `/api/admin/agencies?${toSearchParams({ ...common, name: searchTerm, sortProperty: 'name' })}`;
    case ReportFilterField.Publisher:
      return `/api/publishers?${toSearchParams({ ...common, name: searchTerm, status: [ReportFilterLookupStatus.Active] })}`;
    case ReportFilterField.DemandPartner:
      return `/api/dps?${toSearchParams({
        ...common,
        displayName: searchTerm,
        status: [ReportFilterLookupStatus.Active, ReportFilterLookupStatus.Inactive],
      })}`;
    case ReportFilterField.Placement:
      return `/api/placements?${toSearchParams({ ...common, size: 100, searchTerm, demandIntegrationId, status: [ReportFilterLookupStatus.Active] })}`;
    case ReportFilterField.DemandIntegration:
      return `/api/dps/integrations?${toSearchParams({ ...common, name: searchTerm })}`;
    case ReportFilterField.LineItem:
      return `/api/campaign/line-items?${toSearchParams({ ...common, name: searchTerm, status: [ReportFilterLookupStatus.Active] })}`;
    case ReportFilterField.Application:
      return `/api/apps?${toSearchParams({ ...common, searchTerm })}`;
    case ReportFilterField.Domain:
      return `/api/domains?${toSearchParams({ ...common, url: searchTerm, status: [ReportFilterLookupStatus.Active] })}`;
    default:
      throw new Error(`Report filter ${field} does not have a server-search endpoint.`);
  }
}

function toLookupOptions(
  field: ReportFilterField,
  records: Array<z.infer<typeof lookupRecordSchema>>,
) {
  return records.flatMap((record): ReportFilterOption[] => {
    const option = lookupOption(field, record);
    return option ? [option] : [];
  });
}

function lookupOption(
  field: ReportFilterField,
  record: z.infer<typeof lookupRecordSchema>,
): ReportFilterOption | null {
  const id = lookupOptionId(field, record);
  const label =
    record.name ?? record.displayName ?? record.url ?? record.bundleId ?? record.countryName ?? id;
  return id && label ? { id, label } : null;
}

function lookupOptionId(field: ReportFilterField, record: z.infer<typeof lookupRecordSchema>) {
  if (field === ReportFilterField.Placement) return record.secondaryId;
  if (field === ReportFilterField.LineItem) return record.numericId;
  if (field === ReportFilterField.Country) return record.countryIsoCode;
  if (field === ReportFilterField.Domain) return record.url;
  return record.id;
}
